import type { MetricCatalog } from './api.js';

export const MIN_DISPLAYED_METRICS = 3;

// Not a metric — never appears in `metrics`, never rendered as a column. It's a valid
// `sort` value on its own, selected by clicking the Creative column header.
export const CREATIVE_ID_SORT_FIELD = 'creativeId';

export const CREATIVE_SCORE_METRIC_NAME = 'creative_score';

// Board-level only, like creative_score, but with no per-creative value to show as a table
// column or sort by — so unlike creative_score it's excluded outright rather than special-cased.
export const TOTAL_TAGS_METRIC_NAME = 'total_tags';

export const ACTIVE_DAYS_SORT_FIELD = 'activeDays';
export const SPEND_RATIO_SORT_FIELD = 'spendRatio';

export const DISTINCT_CREATIVES_METRIC_NAME = 'countd_assets';

export const CTR_METRIC_NAME = 'ctr';

const FIXED_SORT_DIRECTIONS: Record<string, SortDirection> = {
  [CREATIVE_ID_SORT_FIELD]: 'asc',
  [ACTIVE_DAYS_SORT_FIELD]: 'desc',
  [SPEND_RATIO_SORT_FIELD]: 'desc',
};

export const DEFAULT_DISPLAYED_METRICS = ['cost', 'ctr', 'roas', 'cp_result', 'cpm', 'cpc', 'cpa'];

export const ELEMENTS_EXPLORER_DEFAULT_DISPLAYED_METRICS = [
  DISTINCT_CREATIVES_METRIC_NAME,
  ...DEFAULT_DISPLAYED_METRICS,
];

export type MetricCategory = MetricCatalog[string]['category'];

// The families the SDK files measures under, in picker order with their display names. A family it
// adds later still groups, under its own name after these: the SDK does not fix the set.
const CATEGORY_LABELS: Record<string, string> = {
  delivery: 'Delivery',
  efficiency: 'Efficiency',
  engagement: 'Engagement',
  video: 'Video',
  conversion: 'Conversion',
  mmp: 'MMP',
  custom: 'Custom',
  composite: 'Scores',
  counts: 'Counts',
};
const KNOWN_CATEGORIES = Object.keys(CATEGORY_LABELS);

export type SortDirection = 'asc' | 'desc';

export interface DisplayedMetrics {
  metrics: string[];
  sort: string;
  direction: SortDirection;
}

export interface MetricGroup {
  category: MetricCategory;
  metrics: string[];
}

export function categoryLabel(category: MetricCategory): string {
  return CATEGORY_LABELS[category] ?? category.charAt(0).toUpperCase() + category.slice(1);
}

function categoryOrder(catalog: MetricCatalog): MetricCategory[] {
  const present = new Set(Object.values(catalog).map((metric) => metric.category));
  return [
    ...KNOWN_CATEGORIES.filter((category) => present.has(category)),
    ...[...present].filter((category) => !(category in CATEGORY_LABELS)).sort(),
  ];
}

export function selectableNames(catalog: MetricCatalog | undefined): string[] {
  if (catalog === undefined) return [];
  return categoryOrder(catalog).flatMap((category) =>
    Object.values(catalog)
      .filter(
        (metric) =>
          metric.selectable &&
          metric.category === category &&
          metric.name !== TOTAL_TAGS_METRIC_NAME,
      )
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((metric) => metric.name),
  );
}

export function defaultDirection(catalog: MetricCatalog | undefined, name: string): SortDirection {
  return catalog?.[name]?.lowerIsBetter === true ? 'asc' : 'desc';
}

function orderedSubset(available: string[], chosen: Iterable<string>): string[] {
  const selectable = new Set(available);
  return Array.from(chosen).filter((name) => selectable.has(name));
}

export function preferredSortMetric(metrics: string[]): string {
  return metrics.includes(CTR_METRIC_NAME) ? CTR_METRIC_NAME : (metrics[0] ?? '');
}

function withSort(catalog: MetricCatalog | undefined, metrics: string[]): DisplayedMetrics {
  const key = preferredSortMetric(metrics);
  return { metrics, sort: key, direction: defaultDirection(catalog, key) };
}

export function resolveDisplayed(
  catalog: MetricCatalog | undefined,
  stored?: DisplayedMetrics,
  defaults: string[] = DEFAULT_DISPLAYED_METRICS,
): DisplayedMetrics {
  const available = selectableNames(catalog);
  const wanted = stored?.metrics ?? defaults;
  const kept = orderedSubset(available, wanted);
  const topUp = available.filter((name) => !kept.includes(name));
  const metrics = [...kept, ...topUp].slice(0, Math.max(kept.length, MIN_DISPLAYED_METRICS));
  if (
    stored !== undefined &&
    (FIXED_SORT_DIRECTIONS[stored.sort] !== undefined || metrics.includes(stored.sort))
  ) {
    return { metrics, sort: stored.sort, direction: stored.direction };
  }
  return withSort(catalog, metrics);
}

export function toggleMetric(
  state: DisplayedMetrics,
  catalog: MetricCatalog | undefined,
  name: string,
): DisplayedMetrics {
  if (!state.metrics.includes(name)) {
    const metrics = orderedSubset(selectableNames(catalog), [...state.metrics, name]);
    return { ...state, metrics };
  }
  return removeMetric(state, catalog, name);
}

export function removeMetric(
  state: DisplayedMetrics,
  catalog: MetricCatalog | undefined,
  name: string,
): DisplayedMetrics {
  if (state.metrics.length <= MIN_DISPLAYED_METRICS) return state;
  const index = state.metrics.indexOf(name);
  if (index === -1) return state;
  const metrics = state.metrics.filter((current) => current !== name);
  if (name !== state.sort) return { ...state, metrics };
  const promoted = metrics[index % metrics.length] ?? '';
  return { metrics, sort: promoted, direction: defaultDirection(catalog, promoted) };
}

/** Adds `name` if needed and moves it to the front. Returns `state` itself when nothing
 * changes (already first, or not in `catalog` yet) — never a new-but-equal object, so a caller
 * can branch on reference equality. */
export function ensureMetricFirst(
  state: DisplayedMetrics,
  catalog: MetricCatalog | undefined,
  name: string,
): DisplayedMetrics {
  if (state.metrics[0] === name) return state;
  if (!state.metrics.includes(name)) {
    const metrics = orderedSubset(selectableNames(catalog), [...state.metrics, name]);
    if (!metrics.includes(name)) return state;
    return moveMetric({ ...state, metrics }, metrics.length - 1, 0);
  }
  return moveMetric(state, state.metrics.indexOf(name), 0);
}

/** `ensureMetricFirst`, and the board also ranks by it. Returns `state` itself when nothing changes. */
export function leadWithMetric(
  state: DisplayedMetrics,
  catalog: MetricCatalog | undefined,
  name: string,
): DisplayedMetrics {
  const first = ensureMetricFirst(state, catalog, name);
  const direction = defaultDirection(catalog, name);
  if (first.metrics[0] !== name || (first.sort === name && first.direction === direction)) {
    return first;
  }
  return { ...first, sort: name, direction };
}

/** The KPIs a creative's detail drawer asks for when no board selection exists: the board's
 * defaults, leading KPI first. Before the catalog is known only the leading KPI is safe to ask for. */
export function detailKpis(catalog: MetricCatalog | undefined, leadingKpi: string): string[] {
  if (catalog === undefined) return [leadingKpi];
  const { metrics } = ensureMetricFirst(resolveDisplayed(catalog), catalog, leadingKpi);
  const kpis = metrics.filter((name) => name !== CREATIVE_SCORE_METRIC_NAME);
  return kpis.includes(leadingKpi) ? kpis : [leadingKpi, ...kpis];
}

export function moveMetric(
  state: DisplayedMetrics,
  fromIndex: number,
  toIndex: number,
): DisplayedMetrics {
  if (fromIndex === toIndex) return state;
  const metrics = [...state.metrics];
  const [moved] = metrics.splice(fromIndex, 1);
  if (moved === undefined) return state;
  metrics.splice(toIndex, 0, moved);
  return { ...state, metrics };
}

export function setSortMetric(
  state: DisplayedMetrics,
  catalog: MetricCatalog | undefined,
  name: string,
): DisplayedMetrics {
  const metrics = state.metrics.includes(name)
    ? state.metrics
    : orderedSubset(selectableNames(catalog), [...state.metrics, name]);
  return { metrics, sort: name, direction: defaultDirection(catalog, name) };
}

export function setSortField(
  state: DisplayedMetrics,
  catalog: MetricCatalog | undefined,
  field: string,
): DisplayedMetrics {
  const fixedDirection = FIXED_SORT_DIRECTIONS[field];
  if (fixedDirection !== undefined) return { ...state, sort: field, direction: fixedDirection };
  return setSortMetric(state, catalog, field);
}

export function nextSort(
  state: DisplayedMetrics,
  catalog: MetricCatalog | undefined,
  field: string,
): DisplayedMetrics {
  if (field === state.sort) {
    return { ...state, direction: state.direction === 'desc' ? 'asc' : 'desc' };
  }
  return setSortField(state, catalog, field);
}

export function clearAllButSort(state: DisplayedMetrics): DisplayedMetrics {
  // Sorting by creativeId doesn't reserve a metrics slot, so the floor comes entirely from filler.
  const isMetricSort = state.metrics.includes(state.sort);
  const fillerCount = isMetricSort ? MIN_DISPLAYED_METRICS - 1 : MIN_DISPLAYED_METRICS;
  const filler = state.metrics.filter((name) => name !== state.sort).slice(0, fillerCount);
  const kept = new Set(isMetricSort ? [state.sort, ...filler] : filler);
  return { ...state, metrics: state.metrics.filter((name) => kept.has(name)) };
}

export function groupMetrics(catalog: MetricCatalog | undefined, query: string): MetricGroup[] {
  const needle = query.trim().toLowerCase();
  const matches = (name: string): boolean => {
    const metric = catalog?.[name];
    if (metric === undefined) return false;
    if (needle === '') return true;
    return (
      metric.label.toLowerCase().includes(needle) ||
      metric.description.toLowerCase().includes(needle)
    );
  };
  const names = selectableNames(catalog).filter(matches);
  if (catalog === undefined) return [];
  return categoryOrder(catalog)
    .map((category) => ({
      category,
      metrics: names.filter((name) => catalog?.[name]?.category === category),
    }))
    .filter((group) => group.metrics.length > 0);
}
